import { NgClass } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  forwardRef,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { omitBy } from 'lodash';
import { DPFormField } from 'src/app/core/models/DPFormField';
import { FormParserService } from 'src/app/core/services/form-parser.service';
import { RadioGroupComponent } from '../radio-group/radio-group.component';
import { ItemClassPipe } from 'src/app/shared/pipes/item-class.pipe';
import { FormControlPipe } from 'src/app/shared/pipes/form-control.pipe';
import { DynamicFieldComponent } from '../../dynamic-field/dynamic-field.component';

/**
 * Yes/No field component.
 *
 * The value is a JSON string of following object:
 * { "field name": "Yes", "subfield1": "value1", "subfield2": "value2", ... }
 * or
 * { "field name": "No" }
 */
@Component({
  selector: 'app-yesno-field',
  standalone: true,
  imports: [
    NgClass,
    ItemClassPipe,
    FormControlPipe,
    forwardRef(() => DynamicFieldComponent),
    ReactiveFormsModule,
  ],
  templateUrl: './yesno-field.component.html',
  styleUrls: ['./yesno-field.component.scss'],
})
export class YesnoFieldComponent implements OnInit {
  @Input() field: DPFormField;
  @Input() control: UntypedFormControl;
  @Input() disabled: boolean;
  @Input() customColumnTpls: Record<string, TemplateRef<any>>;
  @Input() markAsTouched: Subject<void>;

  formGroup: UntypedFormGroup;
  private unsubscribe$ = new Subject<void>();

  constructor(private formParser: FormParserService) {}

  ngOnInit(): void {
    this.field.options = [
      {
        key: 'Yes',
        value: 'Yes',
      },
      {
        key: 'No',
        value: 'No',
      },
    ];
    this.createForm();
    if (this.markAsTouched) {
      this.markAsTouched.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
        this.formGroup.markAllAsTouched();
      });
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Create Yes/No form.
   *
   * The form contains Radio select for "Yes / No" options and subfield if any.
   */
  private createForm() {
    /**
     * Yes/No form fields.
     * It has Yes/No Radio select and subfields.
     */
    const formFields: DPFormField[] = [
      { ...this.field, required: true, subFields: [] },
      ...(this.field.subFields || []).map((subField) => ({
        ...subField,
        required: true,
        parentField: this.field.name,
        subQuestionTriggerOption: 'Yes',
      })),
    ];

    /**
     * Create form and set validators and update the form value.
     */
    this.formGroup = this.formParser.createDynamicForm(formFields, {});
    this.formParser.setValidators(
      formFields,
      this.formGroup,
      this.unsubscribe$
    );
    this.patchValue();

    /**
     * When yesno form value changes,
     * update the form control value so that it value persists in parent form.
     */
    this.formGroup.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((values) => {
        if (this.control) {
          /**
           * Set form control value.
           */
          let newValues =
            values[this.field.name] === 'No'
              ? { [this.field.name]: 'No' }
              : omitBy(values, (v) => !v);
          this.control.setValue(JSON.stringify(newValues));
          this.formGroup.markAllAsTouched();
        }
      });

    /**
     * If form value is "Yes", that means form value was updated before.
     * So mark the field as touched.
     */
    if (this.formGroup.value[this.field.name] === 'Yes') {
      this.formGroup.markAllAsTouched();
    }
  }

  /**
   * Populates yesno form from control value.
   */
  private patchValue() {
    if (!this.field || !this.control) {
      return;
    }

    let value: any;
    try {
      value = JSON.parse(this.control.value || '{}');
    } catch (err) {
      value = {};
    }

    this.formGroup.patchValue(value);
  }
}
